import * as React from "react";
import {
  PageHeader,
  Table,
  Spin,
  Alert,
  Empty,
  Descriptions,
  Tag,
  InputNumber,
  Button,
  Space,
} from "antd";
import {
  SwapOutlined,
  WarningOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { RecommendedMovesClient, RecommendedMoveDto, RecommendedMovesResponseDto } from "../utils/scanner-backend";
import { ApiConfig } from "../utils/AuthorizedApiBase";
import { getApiErrorMessage } from "../utils/apiError";

const recommendedMovesClient = new RecommendedMovesClient(new ApiConfig());

const RecommendedMoves: React.FC = () => {
  const [data, setData] = React.useState<RecommendedMovesResponseDto | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string>("");
  const [analysisPeriodDays, setAnalysisPeriodDays] = React.useState(21);
  const [minimumOrderFrequency, setMinimumOrderFrequency] = React.useState(3);

  // Load recommendations on mount
  React.useEffect(() => {
    loadRecommendations();
  }, []);

  const loadRecommendations = async () => {
    setLoading(true);
    setError("");
    try {
      const result = await recommendedMovesClient.getRecommendedMoves(
        analysisPeriodDays,
        minimumOrderFrequency,
        100
      );
      setData(result);
    } catch (err) {
      console.error("Failed to load recommendations:", err);
      setError(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: "Priorita",
      dataIndex: "PriorityScore",
      key: "PriorityScore",
      width: 120,
      render: (score: number, record: RecommendedMoveDto) => (
        record.IsUrgent ? (
          <Tag color="red" style={{ fontSize: 14, fontWeight: "bold" }}>
            URGENT
          </Tag>
        ) : (
          <Tag color={score > 10 ? "orange" : score > 5 ? "gold" : "blue"} style={{ fontSize: 14 }}>
            {score.toFixed(1)}
          </Tag>
        )
      ),
      sorter: (a: RecommendedMoveDto, b: RecommendedMoveDto) =>
        Number(a.IsUrgent) - Number(b.IsUrgent) || a.PriorityScore - b.PriorityScore,
      defaultSortOrder: "descend" as const,
    },
    {
      title: "Kód produktu",
      dataIndex: "ProductCode",
      key: "ProductCode",
      render: (code: string) => <strong style={{ fontSize: 16 }}>{code}</strong>,
    },
    {
      title: "Názov",
      dataIndex: "ProductName",
      key: "ProductName",
      ellipsis: true,
    },
    {
      title: "Frekvencia",
      dataIndex: "OrderFrequency",
      key: "OrderFrequency",
      width: 120,
      render: (freq: number) => (
        <span style={{ fontSize: 16, fontWeight: 500 }}>{freq} obj.</span>
      ),
      sorter: (a: RecommendedMoveDto, b: RecommendedMoveDto) => b.OrderFrequency - a.OrderFrequency,
    },
    {
      title: "Vydané z 1–4",
      dataIndex: "PickedQuantity",
      key: "PickedQuantity",
      width: 130,
      render: (qty: number, record: RecommendedMoveDto) => (
        <span style={{ fontSize: 14, whiteSpace: "nowrap" }}>
          {qty.toFixed(2)} {record.Unit}
        </span>
      ),
    },
    {
      title: "Z pozície",
      dataIndex: "CurrentPositionId",
      key: "CurrentPositionId",
      width: 160,
      render: (pos: string, record: RecommendedMoveDto) => (
        <Tag color="volcano" style={{ fontSize: 14 }}>
          {pos} ({record.CurrentQuantity} {record.Unit})
        </Tag>
      ),
    },
    {
      title: "Presunúť",
      dataIndex: "QuantityToMove",
      key: "QuantityToMove",
      width: 120,
      render: (qty: number, record: RecommendedMoveDto) => (
        <strong style={{ fontSize: 16, whiteSpace: "nowrap" }}>
          {qty} {record.Unit}
        </strong>
      ),
    },
    {
      title: "Na pozíciu",
      dataIndex: "SuggestedTargetPositionId",
      key: "SuggestedTargetPositionId",
      width: 160,
      render: (pos: string, record: RecommendedMoveDto) => (
        pos === "BUFF" ? (
          <Tag color="magenta" style={{ fontSize: 14, fontWeight: "bold" }}>
            BUFF
          </Tag>
        ) : (
          <Tag color="green" style={{ fontSize: 14 }}>
            {pos} ({record.TargetQuantity} {record.Unit})
          </Tag>
        )
      ),
    },
  ];

  const totalRecommendations = data?.Recommendations.length ?? 0;
  const urgentCount = data?.Recommendations.filter(r => r.IsUrgent).length ?? 0;

  return (
    <div style={{ padding: "0 0 80px 0" }}>
      <PageHeader
        className="site-page-header"
        onBack={() => window.history.back()}
        title="Odporúčané presuny"
        subTitle="Analýza frekvencie výdaja"
      />

      {/* Error Alert */}
      {error && (
        <Alert
          message="Chyba"
          description={error}
          type="error"
          closable
          onClose={() => setError("")}
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Warning if urgent moves exist */}
      {urgentCount > 0 && (
        <Alert
          message="Urgentné presuny!"
          description={`Máte ${urgentCount} urgentných presunov pre expedovateľné objednávky. Tieto produkty sú potrebné pre objednávky pripravené na expedíciu, ale nie sú v dostatočnom množstve v picking miestnostiach (1-4) ani v BUFF.`}
          type="error"
          showIcon
          icon={<WarningOutlined />}
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Filter Controls */}
      <Alert
        message="Nastavenie analýzy"
        description={
          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            <span>
              Doplniť pozíciu sa odporúča, keď je v miestnostiach 1–4 a v BUFF menej, než sa z miestností 1–4
              za obdobie vydalo, a treba presunúť aspoň 3 (pre objednávku pripravenú na expedíciu aj menej).
              Do BUFF len pre objednávky pripravené na expedíciu. Tovar bez pozície v miestnostiach 1–4 sa
              vydáva priamo zo vzdialeného skladu.
            </span>
            <Space wrap>
              <span style={{ fontWeight: 500 }}>Obdobie (dní):</span>
              <InputNumber
                min={1}
                max={365}
                value={analysisPeriodDays}
                onChange={(val) => setAnalysisPeriodDays(val ?? 90)}
                style={{ width: 100 }}
              />
              <span style={{ fontWeight: 500, marginLeft: 16 }}>Min. frekvencia:</span>
              <InputNumber
                min={1}
                max={100}
                value={minimumOrderFrequency}
                onChange={(val) => setMinimumOrderFrequency(val ?? 1)}
                style={{ width: 100 }}
              />
              <Button
                type="primary"
                icon={<ReloadOutlined />}
                onClick={loadRecommendations}
                loading={loading}
              >
                Aktualizovať
              </Button>
            </Space>
          </Space>
        }
        type="info"
        showIcon
        icon={<SwapOutlined />}
        style={{ marginBottom: 16 }}
      />

      {/* Statistics */}
      {data && (
        <Descriptions column={{ xs: 1, sm: 2, md: 4 }} bordered size="small" style={{ marginBottom: 16 }}>
          <Descriptions.Item label="Počet odporúčaní">
            <Tag color={totalRecommendations > 0 ? "blue" : "green"} style={{ fontSize: 16, padding: "4px 12px" }}>
              {totalRecommendations}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Urgentné">
            <Tag color={urgentCount > 0 ? "red" : "green"} style={{ fontSize: 16, padding: "4px 12px" }}>
              {urgentCount}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Analyzované SKU">
            <strong style={{ fontSize: 16 }}>{data.TotalSkusAnalyzed}</strong>
          </Descriptions.Item>
          <Descriptions.Item label="Vzdialený sklad">
            <span style={{ fontSize: 14 }}>Miestnosti: {data.RemoteStorageRooms.join(", ")}</span>
          </Descriptions.Item>
        </Descriptions>
      )}

      {/* Recommendations Table */}
      <Spin spinning={loading}>
        <Table
          dataSource={data?.Recommendations ?? []}
          columns={columns}
          rowKey={(record) =>
            `${record.SkuId}-${record.CurrentPositionId}-${record.BatchNumber}-${record.SuggestedTargetPositionId}`
          }
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => `Celkom ${total} odporúčaní`,
          }}
          scroll={{ x: true }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="Žiadne odporúčané presuny"
              />
            ),
          }}
        />
      </Spin>

      <Button
        style={{ marginTop: 16 }}
        onClick={loadRecommendations}
        loading={loading}
        icon={<ReloadOutlined />}
      >
        Obnoviť
      </Button>
    </div>
  );
};

export default RecommendedMoves;
